import { getChartColor } from '../constants/chartPalette';
import type { Employee } from '../types/employee';

/**
 * 직원별 캘린더 색상 — chartPalette 12색을 등록순으로 자동 배정.
 *
 * createdAt(등록 시각) 오름차순으로 인덱스를 매기므로 직원이 새로 추가돼도
 * 기존 직원의 색이 바뀌지 않는다. 13명째부터는 팔레트가 순환한다.
 */
export function buildEmployeeColorMap(
  employees: Employee[]
): Record<string, string> {
  const sorted = [...employees].sort((a, b) => {
    const at = a.createdAt?.toMillis?.() ?? 0;
    const bt = b.createdAt?.toMillis?.() ?? 0;
    if (at !== bt) return at - bt;
    return a.id.localeCompare(b.id);
  });
  const map: Record<string, string> = {};
  sorted.forEach((employee, index) => {
    map[employee.id] = getChartColor(index);
  });
  return map;
}

/**
 * 칩 배경색에 대비되는 글자색.
 * 브랜드 그린(#00BF40)·오렌지·레드 같은 중간 명도 색에는 디자인 시스템 관행대로
 * 흰 글씨를 유지하고, 라이트 그린/라이트 그레이 톤에만 검은 글씨를 쓴다.
 * 반환 검정값은 designTokens.css 의 --c-cool-950(#171719)과 동기화.
 */
export function chipTextColorFor(backgroundHex: string): string {
  const channel = (offset: number) =>
    parseInt(backgroundHex.slice(offset, offset + 2), 16) / 255;
  const linearize = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const luminance =
    0.2126 * linearize(channel(1)) +
    0.7152 * linearize(channel(3)) +
    0.0722 * linearize(channel(5));
  // 0.45: 팔레트 기준 라이트 톤(green-200/300/500, cool-300)만 검은 글씨가 되는 경계
  return luminance > 0.45 ? '#171719' : '#FFFFFF';
}
